import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CaretUp, MagnifyingGlass, Sliders } from '@phosphor-icons/react';

import Button from 'partial/components/Button';

function StatusFilter({
  value,
  options,
  onChange,
  inputClassName,
  containerClassName,
  dropdownContainerClassName,
  optionsPayloadName,
  ...otherProps
}) {
  const [isShown, setShown] = useState(false);
  const [selected, setSelected] = useState();
  const handleChangeSearch = (e) =>
    onChange((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const handleApplyFilter = () => {
    onChange((prev) => ({
      ...prev,
      is_active: selected,
    }));
    setShown(false);
  };

  return (
    <div
      className={cn(
        'relative flex items-center h-9 border border-neutral-300 rounded bg-white focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2',
        containerClassName,
      )}
    >
      {isShown ? (
        <div className="flex flex-grow px-3 items-center justify-between h-full">
          <div className="flex items-center gap-1.5 text-neutral-700">
            <Sliders size={16} />
            <span className="text-sm font-medium">Filter</span>
          </div>
          <button
            type="button"
            aria-label="Close filter"
            className="text-neutral-500 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 rounded"
            onClick={() => setShown(false)}
          >
            <CaretUp size={14} weight="bold" />
          </button>
          <div
            className={cn(
              'absolute top-full mt-1 rounded-lg border border-neutral-200 shadow-popover z-10 left-0 w-full bg-white py-4 px-3',
              dropdownContainerClassName,
            )}
          >
            <div className="flex justify-between text-xs">
              <h5 className="text-neutral-900 font-semibold mb-2">
                Filter By Status
              </h5>
              <button
                type="button"
                onClick={() => setSelected()}
                className="text-brand-700 font-semibold hover:underline"
              >
                Clear Filter
              </button>
            </div>
            <div className="flex flex-col gap-2 pb-8">
              {options?.map(({ status, label }) => (
                <label
                  key={status}
                  htmlFor={label}
                  className="flex gap-1 items-center cursor-pointer"
                >
                  <input
                    className="form-radio cursor-pointer"
                    name="option"
                    type="radio"
                    onChange={() => setSelected(status)}
                    checked={status === selected}
                    id={label}
                  />
                  <span className="capitalize text-sm text-neutral-700">
                    {label}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-neutral-200">
              <Button secondary size="sm" onClick={() => setShown(false)}>
                Cancel
              </Button>
              <Button primary size="sm" onClick={handleApplyFilter}>
                Apply filter
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center h-full pl-3 pr-1 text-neutral-500">
            <MagnifyingGlass size={16} />
          </div>
          <input
            className={cn(
              'h-full w-[260px] border-0 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-500 focus:ring-0',
              inputClassName,
            )}
            placeholder="Search"
            type="text"
            onChange={handleChangeSearch}
            value={value}
            autoComplete="off"
            {...otherProps}
          />
          <button
            onClick={() => setShown(true)}
            type="button"
            aria-label="Filter by status"
            className="border-l border-neutral-200 px-3 h-full text-neutral-500 hover:text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
          >
            <Sliders size={16} />
          </button>
        </>
      )}
    </div>
  );
}

StatusFilter.defaultProps = {
  containerClassName: '',
  dropdownContainerClassName: '',
  inputClassName: '',
  optionsPayloadName: 'is_active',
  options: [
    { status: 1, label: 'active' },
    { status: 0, label: 'inactive' },
  ],
};

StatusFilter.propTypes = {
  containerClassName: PropTypes.string,
  dropdownContainerClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.instanceOf(Array),
  value: PropTypes.string.isRequired,
  inputClassName: PropTypes.string,
  optionsPayloadName: PropTypes.string,
};

export default StatusFilter;
